import * as React from "react";
import classnames from "classnames";

import { SensorExcess } from "../../../../rest/Dashboard/interfaces";
import { DataGrid, Getter } from "../../../../components/DataGrid/DataGrid";
import { DataGridTextColumn } from "../../../../components/DataGrid/DataGridTextColumn";
import { DataGridTemplateColumn, Props as TemplateProps } from "../../../../components/DataGrid/DataGridTemplateColumn";
import { Button } from "../../../../components/Buttons";
import { Polyglot } from "../../../../polyglot";
import { Key } from "../../../../models";
import { ColumnProps } from "../../../../components/DataGrid/CellColumnProps";
import {MultiselectColumn, MultiselectProps, multiselectPropsFrom} from "../Columns/MultiselectColumn";

const helperStyles = require('../../../../components/BootstrapHelper.local.css');
const tableStyles = require('../../../../components/Table.scss');
const styles = require('./SensorsExcessesGrid.scss');

export interface Props extends MultiselectProps
{
    items: SensorExcess[];
    polyglot: Polyglot;
    sensorsSearchInitiator: Key;
    vehicleSearchInitiator: Key;
    sensorsDataSearchInitiator: Key;
    onVehicle: (datum: any, row: number) => void;
    onSensor: (datum: any, row: number) => void;
    onData: (datum: any, row: number) => void;
    onAssign: (datum: any, row: number) => void;
}

const getDate: Getter<SensorExcess> = se => se.date.toLocaleString();
const getVehicle: Getter<SensorExcess> = se => se.VehicleName;
const getSensor: Getter<SensorExcess> = se => se.SensorName;
const getQuantity: Getter<SensorExcess> = se => se.QuantityName;
const getValue: Getter<SensorExcess> = se => se.Reading;
const getSeverityClass: Getter<SensorExcess> = se => se.Severity === 'red' ? styles.SeverityRed: styles.SeverityYellow;
const getAssignee: Getter<SensorExcess> = se => se.TakenByUserName;

export const SensorsExcessesGrid = (props: Props) => {
    const { items, onVehicle, onSensor, onData, onAssign, polyglot } = props;
    return (
        <DataGrid initialData={items} className={tableStyles.StickyTable} onThead={props.onThead}>
            <MultiselectColumn {...multiselectPropsFrom(props)} datumKey={(se: SensorExcess) => se.IncidentId} />
            <DataGridTextColumn getValue={getDate}
                header={polyglot.t('dashboard.excess_table.column_header.time')} />
            <DataGridTemplateColumn getValue={getVehicle}
                header={polyglot.t('dashboard.excess_table.column_header.vehicle')}
                render={(colProps: TemplateProps) => (
                    <Cell 
                        btnTitle={polyglot.t('dashboard.excess_table.buttons.vehicle')}
                        btnType={'black'}
                        handler={onVehicle}
                        inProgress={(colProps.datum as SensorExcess).IncidentId === props.vehicleSearchInitiator}
                        {...colProps}
                    />
                )} />
            <DataGridTemplateColumn getValue={getSensor}
                header={polyglot.t('dashboard.excess_table.column_header.sensor')}
                render={(colProps: TemplateProps) => (
                    <Cell 
                        btnType={'danger'}
                        btnTitle={polyglot.t('dashboard.excess_table.buttons.sensor')} 
                        handler={onSensor}
                        inProgress={(colProps.datum as SensorExcess).IncidentId === props.sensorsSearchInitiator}
                        {...colProps} 
                    />
                )} />
            <DataGridTextColumn getValue={getQuantity}
                header={polyglot.t('dashboard.excess_table.column_header.quantity')} />
            <DataGridTemplateColumn getValue={getValue}
                header={polyglot.t('dashboard.excess_table.column_header.value')}
                render={(colProps: TemplateProps) => {
                    const { datum, row } = colProps;
                    const excess = datum as SensorExcess;
                    const unitName = excess.UnitName;
                    const inProgress = props.sensorsDataSearchInitiator === excess.IncidentId;
                    const value = getValue(datum);
                    return (
                        <td>
                            <div className={styles.Cell}>
                                <span>
                                    <span className={getSeverityClass(datum)}>{unitName ? `${value} ${unitName}` : value}</span>
                                </span>
                                <Button dim="xs" type="black"
                                    onClick={e => onData(datum, row)}
                                    spinner={inProgress} disabled={inProgress}>
                                    {polyglot.t('dashboard.excess_table.buttons.data')}
                                </Button>
                            </div>
                        </td>
                    );
                }} />
            <DataGridTemplateColumn
                header={polyglot.t('dashboard.excess_table.column_header.assignee')}
                render={(colProps: TemplateProps) => {
                    const { datum, row } = colProps;
                    const assignee = getAssignee(datum);
                    return (
                        <td>
                            { assignee
                                ? <span>{assignee}</span>
                                : <Button type="success" leftIcon="pushpin" dim="xs" onClick={e => onAssign(datum, row)}>{polyglot.t('dashboard.excess_table.buttons.iamonit')}</Button>
                            }
                        </td>
                    );
                }}
            />

        </DataGrid>
    );
};

interface CellProps extends TemplateProps
{
    handler: (datum: any, row: number) => void;
    btnTitle: string;
    inProgress: boolean;
    btnType?: any;
}

const Cell = (props: CellProps) => {
    const { datum, row, getValue, handler, btnTitle, inProgress, btnType } = props;
    return (
        <td>
            <div className={styles.Cell}>
                <span>{getValue(datum)}</span>
                <Button dim="xs" type={btnType} leftIcon="search" spinner={inProgress} disabled={inProgress} onClick={e => handler(datum, row)}>{btnTitle}</Button>
            </div>
        </td>
    );
};


// WEBPACK FOOTER //
// ./src/app/components/Pages/Home/SensorsExcessesGrid.tsx